import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toaster } from '@optiaxiom/react';
import { useQueryClient } from '@tanstack/react-query';

import { useNotificationFeatures } from '@frontend/features/notifications/hooks';
import { getDeepLinkPath, getNotificationIntent } from '@frontend/features/notifications/utils';

import { useInstanceId } from './useInstanceId';
import { useNamespacedNavigate } from './useNamespacedNavigate';
import { useSocketChannel } from './useSocket';

const IM_EVENT = {
  MODE_CHANGED: 'opal::im::modeChanged',
  CONVERSATION_CHANGED: 'opal::im::conversationChanged',
  NOTIFICATION_CLICKED: 'opal::im::notificationClicked',
} as const;

// Module-level singleton updated by chat-frontend events. Intentionally
// outside useEffect so state persists across component remounts.
const messagingViewState = { tab: '', conversationId: '' };

document.addEventListener(IM_EVENT.MODE_CHANGED, (e: Event) => {
  const detail = (e as CustomEvent).detail as { mode: string } | undefined;
  messagingViewState.tab = detail?.mode ?? '';
});

document.addEventListener(IM_EVENT.CONVERSATION_CHANGED, (e: Event) => {
  const detail = (e as CustomEvent).detail as { conversationId: string } | undefined;
  messagingViewState.conversationId = detail?.conversationId ?? '';
});

type NotificationCreatedData = {
  notification_id: string;
  opal_instance_id: string;
  type: string;
  title: string;
  message: string;
  agent_id?: string;
  metadata?: {
    execution_id?: string;
    thread_id?: string;
    [key: string]: unknown;
  };
  created_at: string;
};

export function useNotificationEvents() {
  const { instanceId } = useInstanceId();
  const location = useLocation();
  const navigate = useNamespacedNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { badgeEnabled, groupingEnabled } = useNotificationFeatures();

  const onNotificationCreated = useCallback(
    (data: unknown) => {
      const notification = data as NotificationCreatedData;

      // Ignore notifications for other instances
      if (notification.opal_instance_id !== instanceId) {
        return;
      }

      // Skip optimistic +1 in grouped mode — the new notification may join an
      // existing group, in which case the count shouldn't change. Refetch reconciles.
      if (!groupingEnabled) {
        queryClient.setQueriesData<number>(
          { queryKey: ['notifications', 'unread-count'] },
          currentCount => (currentCount == null ? currentCount : currentCount + 1)
        );

        if (badgeEnabled) {
          queryClient.setQueriesData<number>(
            { queryKey: ['notifications', 'badge-count'] },
            currentCount => (currentCount == null ? currentCount : currentCount + 1)
          );
        }
      }

      void queryClient.invalidateQueries({ queryKey: ['notifications'] });

      // Suppress toast if user is viewing the relevant thread
      if (notification.metadata?.thread_id) {
        const currentThreadId = searchParams.get('threadId');
        if (currentThreadId === notification.metadata.thread_id) {
          return;
        }
      }

      if (notification.type?.startsWith('messaging_')) {
        const notifConversationId = (notification.metadata as Record<string, unknown>)
          ?.conversation_id as string | undefined;
        if (
          messagingViewState.tab === 'messages' &&
          messagingViewState.conversationId &&
          messagingViewState.conversationId === notifConversationId
        ) {
          return;
        }
      }

      // Suppress toast if user is viewing the relevant execution
      if (notification.metadata?.execution_id) {
        const isOnExecutionPage = location.pathname.includes(
          `/executions/${notification.metadata.execution_id}`
        );
        if (isOnExecutionPage) {
          return;
        }
      }

      toaster.create(notification.title, {
        action: 'View',
        intent: getNotificationIntent(notification.type),
        onAction: () => {
          if (notification.type?.startsWith('messaging_')) {
            const meta = notification.metadata as Record<string, unknown> | undefined;
            document.dispatchEvent(
              new CustomEvent(IM_EVENT.NOTIFICATION_CLICKED, {
                detail: {
                  conversationId: meta?.conversation_id,
                  messageId: meta?.message_id,
                  msgThreadId: meta?.thread_id,
                },
                bubbles: true,
              })
            );
          }
          navigate(getDeepLinkPath(notification));
        },
      });
    },
    [
      badgeEnabled,
      groupingEnabled,
      instanceId,
      location.pathname,
      navigate,
      queryClient,
      searchParams,
    ]
  );

  useSocketChannel('notification_created', onNotificationCreated);

  const onBadgeSeen = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['notifications', 'badge-count'] });
  }, [queryClient]);

  useSocketChannel('badge_seen', onBadgeSeen);
}
